'use client';
// Per-holding hedge analysis. Top: position-summary table with RSI + momentum
// + Hedge button. Click a row → expanded panel below with a 90-day price
// chart on the left and a 6-strategy menu on the right. Each strategy is a
// click-to-expand card showing rationale, leg-by-leg breakdown, payoff
// scenarios, and key numbers.

import { useState } from 'react';
import { ProductionHedger } from './_hedge/ProductionHedger';
import { useResource } from '@/lib/use-resource';
import type { HedgeStrategy } from '@/lib/hedge-structures';
import { HedgeChart } from './HedgeChart';
import { StockChartCombined } from './StockChartCombined';
import { StrategyCard } from './_hedge/StrategyCard';

interface Holding {
  symbol: string;
  sector: string | null;
  spot: number;
  weightPct: number;
  rsiDaily: number | null;
  rsiWeekly: number | null;
  rsiMonthly: number | null;
  momentumZ: number | null;
  structures: HedgeStrategy[];
  noChain?: boolean;
}

interface ApiResponse {
  asOf: string | null;
  holdings: Holding[];
  error?: string;
}

function rsiTone(rsi: number | null): { text: string; bg: string; label: string } {
  if (rsi == null) return { text: 'text-[#64748b]', bg: 'bg-[#1e293b]/40', label: '—' };
  if (rsi >= 80) return { text: 'text-red-300', bg: 'bg-red-500/15', label: rsi.toFixed(0) };
  if (rsi >= 70) return { text: 'text-amber-300', bg: 'bg-amber-500/15', label: rsi.toFixed(0) };
  if (rsi <= 30) return { text: 'text-emerald-300', bg: 'bg-emerald-500/15', label: rsi.toFixed(0) };
  if (rsi <= 40) return { text: 'text-emerald-200', bg: 'bg-emerald-500/8', label: rsi.toFixed(0) };
  return { text: 'text-[#cbd5e1]', bg: 'bg-[#1e293b]/40', label: rsi.toFixed(0) };
}

export function HedgeLabTable({ initialSymbol, initialStrategy }: { initialSymbol?: string | null; initialStrategy?: string | null } = {}) {
  const { data, error } = useResource<ApiResponse>('/api/hedge-lab');
  const [openSym, setOpenSym] = useState<string | null>(initialSymbol ?? null);
  // initialStrategy is consumed by the row-level component to pre-select a card. Pass via prop drill.

  if (error) return <div className="mt-10 text-sm text-red-300">Failed to load: {String(error)}</div>;
  if (!data) return <div className="mt-10 text-sm text-[#64748b]">Loading…</div>;
  if (data.error) return <div className="mt-10 text-sm text-red-300">{data.error}</div>;

  // Focused mode: deep-link from the 🛡 pill in /portfolio Holdings table.
  // Steve directive 2026-05-09: when a single symbol is in the URL, drop the
  // 15-name holdings list entirely and render only that name's view —
  // price chart → RSI/momentum sub-chart → strategy cards stacked.
  if (initialSymbol) {
    const h = data.holdings.find((x) => x.symbol === initialSymbol);
    if (!h) {
      return (
        <div className="mt-6 text-[12px] text-[#94a3b8]">
          {initialSymbol} is not in the current holdings list. <a href="/hedge-lab" className="text-amber-300 underline">View all holdings →</a>
        </div>
      );
    }
    const preselectedId = initialStrategy ? h.structures.find((s) => s.type === initialStrategy)?.id : undefined;
    return <FocusedView holding={h} initialOpenId={preselectedId} asOf={data.asOf} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-[#94a3b8]">{data.holdings.length} holdings · click <strong className="text-white">Hedge</strong> to see strategies</span>
        {data.asOf && (
          <span className="text-[#64748b] font-mono">option chain as of {new Date(data.asOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
        )}
      </div>

      <div className="border border-[#1e293b] rounded-lg overflow-hidden bg-[#0f141c]">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0e14] border-b border-[#1e293b]">
            <tr className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
              <th className="text-left px-3 py-1.5 font-semibold w-[80px]">Symbol</th>
              <th className="text-left px-2 py-1.5 font-semibold w-[130px]">Sector</th>
              <th className="text-right px-2 py-1.5 font-semibold w-[70px]">Weight</th>
              <th className="text-right px-2 py-1.5 font-semibold w-[70px]">Spot</th>
              <th className="text-center px-1 py-1.5 font-semibold" colSpan={3}>RSI · D / W / M</th>
              <th className="text-right px-2 py-1.5 font-semibold w-[80px]">12-1 Mom z</th>
              <th className="text-right px-2 py-1.5 font-semibold w-[100px]">Hedge</th>
            </tr>
          </thead>
          <tbody>
            {data.holdings.map((h) => {
              const isOpen = openSym === h.symbol;
              const rD = rsiTone(h.rsiDaily);
              const rW = rsiTone(h.rsiWeekly);
              const rM = rsiTone(h.rsiMonthly);
              return (
                <tr key={h.symbol} className="border-t border-[#1e293b]/50 leading-tight">
                  <td className="px-3 py-1 font-mono font-semibold text-white text-[12px]">{h.symbol}</td>
                  <td className="px-2 py-1 text-[#94a3b8] text-[11px]">{h.sector ?? '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-white text-[12px]">{h.weightPct.toFixed(1)}%</td>
                  <td className="px-2 py-1 text-right tabular-nums text-[#cbd5e1] text-[12px]">${h.spot.toFixed(2)}</td>
                  <td className={`px-1 py-1 text-center text-[11px] tabular-nums font-semibold ${rD.text} ${rD.bg}`}>{rD.label}</td>
                  <td className={`px-1 py-1 text-center text-[11px] tabular-nums font-semibold ${rW.text} ${rW.bg}`}>{rW.label}</td>
                  <td className={`px-1 py-1 text-center text-[11px] tabular-nums font-semibold ${rM.text} ${rM.bg}`}>{rM.label}</td>
                  <td className={`px-2 py-1 text-right tabular-nums text-[12px] ${h.momentumZ == null ? 'text-[#64748b]' : (h.momentumZ > 1.5 ? 'text-red-300' : h.momentumZ < -1 ? 'text-emerald-300' : 'text-[#cbd5e1]')}`}>
                    {h.momentumZ != null ? (h.momentumZ >= 0 ? '+' : '') + h.momentumZ.toFixed(2) : '—'}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {h.structures.length === 0 ? (
                      <span className="text-[10px] text-[#64748b]">no strategies</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setOpenSym(isOpen ? null : h.symbol)}
                        className={`text-[10px] px-2.5 py-0.5 rounded font-semibold uppercase tracking-wider transition-colors ${
                          isOpen
                            ? 'bg-amber-400/20 text-amber-300 border border-amber-400/50'
                            : 'bg-[#1e293b] text-[#cbd5e1] border border-[#334155] hover:border-amber-400/50 hover:text-amber-300'
                        }`}
                      >
                        {isOpen ? '✕ close' : '↗ hedge'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openSym && (() => {
        const h = data.holdings.find((x) => x.symbol === openSym);
        if (!h) return null;
        // Map deep-link strategy slug → strategy id. e.g., ?open=collar → expand the collar card.
        const preselectedId = (initialStrategy && openSym === initialSymbol)
          ? h.structures.find((s) => s.type === initialStrategy)?.id
          : undefined;
        return <ExpandedPanel holding={h} onClose={() => setOpenSym(null)} initialOpenId={preselectedId} />;
      })()}
    </div>
  );
}

function ExpandedPanel({ holding, onClose, initialOpenId }: { holding: Holding; onClose: () => void; initialOpenId?: string }) {
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? holding.structures[0]?.id ?? null);
  return (
    <div className="border border-amber-400/40 rounded-lg bg-[#0f141c] p-4 mt-2">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <span className="text-[14px] font-semibold text-amber-300 font-mono">{holding.symbol}</span>
          <span className="text-[12px] text-[#94a3b8] ml-3">spot ${holding.spot.toFixed(2)} · weight {holding.weightPct.toFixed(1)}%</span>
        </div>
        <button onClick={onClose} className="text-[10px] uppercase tracking-wider text-[#64748b] hover:text-white">close</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[480px_minmax(0,1fr)] gap-4">
        <div>
          <HedgeChart
            symbol={holding.symbol}
            strategy={holding.structures.find((s) => s.id === openId) ?? null}
            daysBack={90}
          />
          <div className="mt-3 text-[10px] text-[#64748b] leading-relaxed">
            Click any strategy on the right — the chart redraws with that
            strategy&rsquo;s strike lines (red = call leg / cap, green = put leg / floor).
            Trim has no option legs; you&rsquo;ll see only the spot line.
            Premium quoted at bid (when selling) and ask (when buying) — broker
            mid-market will be slightly tighter.
          </div>
        </div>
        <div className="space-y-2">
          {holding.structures.map((s) => (
            <StrategyCard
              key={s.id}
              strategy={s}
              expanded={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Focused single-stock view — entered by deep-link from /portfolio's 🛡 pill.
 * Skip the holdings list, show price chart → momentum/RSI → strategy cards
 * stacked full-width.
 */
function FocusedView({ holding, initialOpenId, asOf }: { holding: Holding; initialOpenId?: string; asOf?: string | null }) {
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? holding.structures[0]?.id ?? null);
  const [period, setPeriod] = useState<'D' | 'W' | 'M'>('D');
  const selectedStrategy = holding.structures.find((s) => s.id === openId) ?? null;
  const { data: hedgeData } = useResource<{ signals?: Record<string, { asymZ: number; asymmetry: number | null; mom12: number; firedAt: string }> }>('/api/hedge-signals');
  const signal = hedgeData?.signals?.[holding.symbol] ?? null;
  const bestFit = holding.structures[0] ?? null;
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3 border-b border-[#1e293b] pb-3">
        <div>
          <span className="text-[20px] font-semibold text-amber-300 font-mono">{holding.symbol}</span>
          <span className="text-[13px] text-[#94a3b8] ml-3">spot ${holding.spot.toFixed(2)} · weight {holding.weightPct.toFixed(1)}% · sector {holding.sector ?? '—'}</span>
        </div>
        <a href="/hedge-lab" className="text-[10px] uppercase tracking-wider text-[#64748b] hover:text-white">← all holdings</a>
      </div>

      {/* Why hedge — explain in plain words at the top. Honest about the
          universe-relative nature of the asymZ trigger: a stock can have a
          decent raw asymmetry (e.g., 4:1) but still fire the signal because
          the rest of the universe has BETTER risk/reward right now. */}
      <div className="border border-amber-500/30 rounded-lg bg-amber-500/5 p-4">
        <div className="text-amber-300 text-[13px] font-bold mb-2 uppercase tracking-wide">Why hedge {holding.symbol}</div>
        <div className="text-[12.5px] text-[#cbd5e1] leading-relaxed space-y-2">
          {signal ? (
            <>
              <p>
                <strong className="text-amber-200">The two conditions that fired:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-[12px] ml-2">
                <li>
                  Asymmetry ratio <strong className="text-white">{signal.asymmetry != null ? `${signal.asymmetry.toFixed(1)}:1` : '—'}</strong>{' '}
                  (z-score {signal.asymZ.toFixed(2)}). Raw 4:1 would clear your entry bar in absolute terms,
                  but the z-score is universe-relative — it means {holding.symbol}&apos;s risk/reward is
                  <em className="text-white"> below the universe median</em> right now. The model has{' '}
                  <em className="text-white">cheaper</em> places to put the same dollar.
                </li>
                <li>
                  12-month price return <strong className="text-white">+{(signal.mom12 * 100).toFixed(0)}%</strong>{' '}
                  (above the +30% &ldquo;already-extended&rdquo; threshold). The stock has run hot.
                </li>
              </ul>
              <p>
                Both together: a name that has run AND no longer offers the best risk/reward in the universe —
                historically the worst time to ride naked. The signal does <em>not</em> say {holding.symbol} is
                a sell in absolute terms; it says &ldquo;trim into strength when you have better alternatives.&rdquo;
              </p>
            </>
          ) : (
            <p>This view is loaded by deep-link, but the live hedge-signal record for {holding.symbol} isn&apos;t firing right now. The strategies below are still useful as a reference set.</p>
          )}
          <p>
            <strong className="text-amber-200">Backtest evidence (2015–2024, 10 vintages):</strong>{' '}
            dampening positions when this combined signal fires lifts the alpha basket&apos;s
            Sharpe from <strong className="text-white">0.799 → 0.851</strong> (+0.052), clips
            the worst-year drawdown from <strong className="text-white">−16.4% → −14.3%</strong>{' '}
            (+2.1pp), at a CAGR cost of <strong className="text-white">−2.6pp</strong>. Roughly
            2.6 fires/year on a 20-name basket.
          </p>
          {bestFit && (
            <p>
              <strong className="text-amber-200">Best-fit strategy ({bestFit.fitScore}/100, {bestFit.fitTier}):</strong>{' '}
              <span className="text-white">{bestFit.name}</span>.{bestFit.fitReason ? ` ${bestFit.fitReason}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Period toggle — Bloomberg style (D/W/M) — drives bar frequency on
          both the price chart and the RSI/momentum sub-chart. */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-[#64748b]">Period:</span>
        {(['D', 'W', 'M'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={`text-[11px] px-3 py-1 rounded font-semibold uppercase tracking-wider transition-colors ${
              period === p
                ? 'bg-amber-400/20 text-amber-300 border border-amber-400/50'
                : 'bg-[#1e293b] text-[#cbd5e1] border border-[#334155] hover:border-amber-400/50 hover:text-amber-300'
            }`}
          >
            {p === 'D' ? 'Daily' : p === 'W' ? 'Weekly' : 'Monthly'}
          </button>
        ))}
      </div>

      {/* Single combined chart — price (HLC, top, larger) + RSI/momentum
          (bottom, smaller). Synced X-axis + cursor via Recharts syncId so the
          two panels move together when scrubbing. Steve directive 2026-05-09:
          one larger chart, no profit/loss zones, just HLC and the bottom
          oscillator panel. */}
      <StockChartCombined symbol={holding.symbol} strategy={selectedStrategy} period={period} priceHeight={378} oscHeight={162} />

      {/* Production hedger — only shown when the user has opened a collar.
          Replaces the per-share textbook view with position-level Greeks,
          tax flags, stress matrix, and roll triggers. Added 2026-05-10. */}
      {selectedStrategy && selectedStrategy.type === 'collar' && (
        <ProductionHedger
          symbol={holding.symbol}
          spot={holding.spot}
          weightPct={holding.weightPct}
          strategy={selectedStrategy}
          chainAsOf={asOf ?? null}
        />
      )}

      <div>
        <div className="text-[12px] uppercase tracking-wide text-[#94a3b8] font-semibold mb-2">
          Hedge strategies — ranked
        </div>
        <div className="space-y-2">
          {holding.structures.map((s) => (
            <StrategyCard
              key={s.id}
              strategy={s}
              expanded={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
        </div>
        <p className="text-[10px] text-[#64748b] mt-3 leading-relaxed">
          Click any strategy — the price chart above redraws with that strategy&rsquo;s strike lines (red = call leg / cap, green = put leg / floor).
          Premium quoted at bid (selling) and ask (buying).
          {asOf && ` Option chain as of ${new Date(asOf).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
        </p>
      </div>
    </div>
  );
}
